<script setup lang="ts">
import DeleteIcon from '~/assets/icon/delete.svg'
import CloseIcon from '~/assets/icon/close.svg'
import StarIcon from '~/assets/icon/star.svg'
import ArrowDownIcon from '~/assets/icon/arrow-down.svg'
import { normalizeLocaleParam, type LocaleCode } from '@packages/i18n'
import { formatScoreValue } from '~/utils/formatters'
import GlobalIcon from '~/assets/icon/global.svg'
import ServiceIcon from '~/assets/icon/service.svg'
import DownloadIcon from '~/assets/icon/download.svg'
import SearchIcon from '~/assets/icon/search.svg'
import WikiBitStoreWhiteLogo from '~/assets/logo/wikibit-store-footer.svg'
import WikiBitStoreLogo from '~/assets/logo/wikibit-store.svg'
import { FALLBACK_DOWNLOAD_LINKS } from '~/api/download'
import type { SearchHistoryItem, TraderListItem } from '~/types/home'
import { resolveExternalUrl } from '~/utils/externalUrl'

type UserMenuItem = {
  label: string
  href: string
  action?: 'logout'
  danger?: boolean
  key?: string
}

const props = withDefaults(
  defineProps<{
    scrollFadeDistance?: number
    useWhiteLogo?: boolean
  }>(),
  {
    scrollFadeDistance: 0,
    useWhiteLogo: false,
  },
)

const { locales, localePath, switchLocalePath, t, locale } = useAppI18n()
const matomo = useMatomo()
const languageCodeCookie = useAppCookie('LanguageCode')

const WIKIBIT_MAIN_ORIGIN = 'https://www.wikibit.com'

const afTypeMap = new Map([
  ['wikibitHome', 'WikiBit Home'],
  ['usercener', '个人中心'],
  ['Search', '搜索'],
  ['Language', '语言选择'],
  ['Support', '联系我们'],
  ['Download', '下载APP'],
])

const actionButtons = [{ label: 'Search' }, { label: 'Language' }, { label: 'Support' }, { label: 'Download' }] as const

const route = useRoute()

const DOWNLOAD_AUTO_POPUP_DISMISS_KEY = 'wikibit-pc-download-popover-auto-shown'
const DOWNLOAD_AUTO_POPUP_SCROLL_THRESHOLD = 654

/** 全局共享下载地址，SSR 预取。 */
const { data: appDownloadLinks } = await useAppDownloadLinks()
const downloadQrUrl = computed(
  () =>
    appDownloadLinks.value?.data?.h5down ||
    (appDownloadLinks.value as unknown as { h5down?: string } | null)?.h5down ||
    FALLBACK_DOWNLOAD_LINKS.data.h5down,
)
const appStore = useAppStore()
const { clearLoginCookies } = useLogin()
const LANGUAGE_PROMPT_DISMISS_KEY = 'wikibit-pc-language-prompt-dismissed-date'
const browserLocale = ref<LocaleCode | null>(null)
const browserLocalePromptText = ref('')
const browserLocalePromptConfirmText = ref('')
const showLanguagePrompt = ref(false)
const storedUserAvatar = ref('')
const isDownloadAutoVisible = ref(false)
const isDownloadPopoverHoverSuppressed = ref(false)
const scrollProgress = ref(props.scrollFadeDistance > 0 ? 0 : 1)

const userMenuRef = ref<HTMLElement | null>(null)
const isUserMenuOpen = ref(false)
const showLogoutConfirmDialog = ref(false)
const isNewsDetailRoute = computed(() => route.path.includes('/news/'))
const shouldShowWhiteLogo = computed(() => props.useWhiteLogo && scrollProgress.value < 1)
const languagePromptText = computed(() => browserLocalePromptText.value || t('82388'))
const languagePromptConfirmText = computed(() => browserLocalePromptConfirmText.value || t('82389'))
const wikiBitHomeUrl = computed(() => resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, localePath('/')))
const wikiBitSupportUrl = computed(() => resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, '/contact', locale))
const wikiBitDownloadUrl = computed(() => resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, '/download', locale))

const normalizeBrowserLocale = (value: string): LocaleCode | null => {
  const normalizedValue = value.trim().replaceAll('_', '-').toLowerCase()

  if (!normalizedValue) {
    return null
  }

  const candidate =
    normalizedValue !== 'zh-cn' && normalizedValue.includes('-')
      ? (normalizedValue.split('-')[0] ?? '')
      : normalizedValue

  return normalizeLocaleParam(candidate)
}

const getNaturalDayKey = () => {
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = `${currentDate.getMonth() + 1}`.padStart(2, '0')
  const day = `${currentDate.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const hideLanguagePrompt = () => {
  showLanguagePrompt.value = false
}

const syncBrowserLocalePromptMessages = async (targetLocale: LocaleCode | null) => {
  if (!targetLocale) {
    browserLocalePromptText.value = ''
    browserLocalePromptConfirmText.value = ''
    return
  }

  const [promptText, promptConfirmText] = await Promise.all([
    getSeoLocaleMessage(targetLocale, '82388'),
    getSeoLocaleMessage(targetLocale, '82389'),
  ])

  browserLocalePromptText.value = promptText
  browserLocalePromptConfirmText.value = promptConfirmText
}

const handleCloseLanguagePrompt = () => {
  matomo.trackEvent('home_language_confirm', {
    af_type: '关闭',
  })
  if (import.meta.client) {
    window.localStorage.setItem(LANGUAGE_PROMPT_DISMISS_KEY, getNaturalDayKey())
  }
  hideLanguagePrompt()
}

const handleSwitchToBrowserLocale = () => {
  matomo.trackEvent('home_language_confirm', {
    af_type: '切换',
  })
  if (!browserLocale.value) {
    return
  }

  if (import.meta.client) {
    window.localStorage.setItem(LANGUAGE_PROMPT_DISMISS_KEY, getNaturalDayKey())
  }
  hideLanguagePrompt()
  const targetUrl = switchLocalePath(browserLocale.value)

  if (import.meta.client) {
    window.location.href = targetUrl
  }
}

const handleLanguageItemClick = (code: string) => {
  // 项目里不用，给 akamai 用的，误删
  languageCodeCookie.value = code
  matomo.trackEvent('home_language', {
    af_type: code,
  })
  hideLanguagePrompt()
}

const openLoginModal = () => {
  appStore.setLoginShow(true)
}

// 首屏脚本负责 Cookie 快速显示，Vue 使用共享 Store 保证登录、登出状态同步。
const isLoggedIn = computed(() => import.meta.client && Boolean(String(appStore.user?.userId ?? '').trim()))
const resolvedUserAvatar = computed(() => storedUserAvatar.value.trim())

useHead({
  script: [
    {
      tagPosition: 'bodyClose',
      innerHTML: `
        (() => {
          const loginEntry = document.querySelector('[data-auth-login]')
          const userEntry = document.querySelector('[data-auth-user]')
          const authEntries = [loginEntry, userEntry].filter(Boolean)
          let authEntrySlot = loginEntry?.parentElement?.matches('.auth-entry-slot')
            ? loginEntry.parentElement
            : null

          // Cached HTML may predate the wrapper. Normalize it before Vue hydrates.
          if (!authEntrySlot && loginEntry?.parentElement) {
            authEntrySlot = document.createElement('div')
            authEntrySlot.className = 'auth-entry-slot'
            authEntrySlot.style.cssText = 'display:flex;align-items:center;min-width:82px;min-height:40px;'
            loginEntry.parentElement.insertBefore(authEntrySlot, loginEntry)
            authEntries.forEach((entry) => authEntrySlot.append(entry))
          }

          const cookies = document.cookie.split(';').map((item) => item.trim())
          const hasUser = cookies.some((item) => item.startsWith('userid=') && item.slice(7))
          if (loginEntry) loginEntry.style.display = hasUser ? 'none' : ''
          if (userEntry) userEntry.style.display = hasUser ? '' : 'none'
          if (!hasUser) return
          try {
            const storedUser = JSON.parse(localStorage.getItem('shared-app-user') || '{}')
            const avatar = typeof storedUser.avatar === 'string' ? storedUser.avatar.trim() : ''
            const avatarContainer = document.querySelector('[data-auth-avatar]')
            const avatarElement = avatarContainer?.querySelector('img:not([aria-hidden="true"])')
            if (avatarElement && avatarContainer && avatar) {
              const fallbackElement = avatarContainer.querySelector('img[aria-hidden="true"]')
              avatarContainer.style.display = ''
              avatarElement.setAttribute('src', avatar)
              avatarElement.classList.remove('invisible')
              avatarElement.classList.add('visible')
              if (fallbackElement) {
                fallbackElement.classList.remove('visible')
                fallbackElement.classList.add('invisible')
              }
              avatarContainer.setAttribute('data-auth-avatar-src', avatar)
            }
          } catch {}
        })()
      `,
    },
  ],
})

const userMenuItems = computed<UserMenuItem[]>(() => [
  // {
  //   label: t('82392'),
  //   href: resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, localePath('/account/posts')),
  //   key: '我的发布',
  // },
  // {
  //   label: t('84550'),
  //   href: resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, localePath('/account/following')),
  //   key: '我的监控',
  // },
  // {
  //   label: t('82394'),
  //   href: resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, localePath('/account')),
  //   key: '个人设置',
  // },
  {
    label: t('82395'),
    href: 'javascript:void(0)',
    key: '退出登录',
    action: 'logout',
    danger: true,
  },
])

const closeUserMenu = () => {
  isUserMenuOpen.value = false
}

const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value
}

const openUserMenu = () => {
  isUserMenuOpen.value = true
}

const closeLogoutConfirmDialog = (text?: string) => {
  if (text) {
    matomo.trackEvent('home_logout_pop', {
      af_type: text,
    })
  }

  showLogoutConfirmDialog.value = false
}

const openLogoutConfirmDialog = () => {
  showLogoutConfirmDialog.value = true
}

const confirmLogout = () => {
  matomo.trackEvent('home_logout_pop', {
    af_type: '确定',
  })
  clearLoginCookies()
  storedUserAvatar.value = ''
  closeLogoutConfirmDialog()
  if (route.path.includes('/account')) {
    navigateTo(localePath('/'), { external: true })
  }
}

const handleUserMenuItemClick = (event: MouseEvent, item: UserMenuItem) => {
  matomo.trackEvent('home_profile', {
    af_type: item.key,
  })
  if (item.action === 'logout') {
    event.preventDefault()
    openLogoutConfirmDialog()
    closeUserMenu()
    return
  }

  closeUserMenu()
}

const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof Node)) {
    return
  }

  if (userMenuRef.value?.contains(target)) {
    return
  }

  closeUserMenu()
}

const handleSupportClick = async () => {
  // 从顶部导航进入联系我们页时，记录顶部入口归因。
  matomo.trackEvent('contact_us_page', {
    af_source: '顶部',
  })

  await navigateTo(wikiBitSupportUrl.value, {
    external: true,
    open: {
      target: '_blank',
    },
  })
}

const handleDownloadClick = async () => {
  await navigateTo(wikiBitDownloadUrl.value, {
    external: true,
    open: {
      target: '_blank',
    },
  })
}

/** 从本地用户快照恢复头像，兼容刷新后 Store 尚未回填的场景。 */
const readStoredUserAvatar = () => {
  if (import.meta.server) {
    return ''
  }

  try {
    const rawStoredUser = window.localStorage.getItem('shared-app-user')

    if (!rawStoredUser) {
      return ''
    }

    const parsedStoredUser = JSON.parse(rawStoredUser) as { avatar?: unknown }
    return typeof parsedStoredUser.avatar === 'string' ? parsedStoredUser.avatar.trim() : ''
  } catch {
    return ''
  }
}

const isNavSearchClicked = ref(false)

const {
  searchQuery: navSearchQuery,
  isFocused: isNavSearchFocused,
  searchHistory: navSearchHistory,
  searchDefaultData: navSearchDefaultData,
  popularTraders: navPopularRecommendations,
  hasSearched: hasNavSearched,
  fetchSearchSuggest: fetchNavSearchSuggest,
  handleSearch: handleNavSearch,
  onBlur: handleBaseNavSearchBlur,
  selectHistoryItem: selectNavSearchHistory,
  selectTraderItem: selectNavTraderItem,
  deleteHistoryItem: deleteNavSearchHistory,
  clearAllHistory: clearNavSearchHistory,
  initSearchHistory,
} = useHomeSearch({
  immediateDefault: false,
  externalOrigin: WIKIBIT_MAIN_ORIGIN,
})

// URL 中的搜索关键词需要在头部搜索框回显，并随查询参数变化同步更新。
watch(
  () => route.query.keyword,
  (keyword) => {
    const firstKeyword = Array.isArray(keyword) ? keyword[0] : keyword
    navSearchQuery.value = typeof firstKeyword === 'string' ? firstKeyword.trim() : ''
  },
  { immediate: true },
)

// 导航搜索框在吸附态默认展开，在首页顶部则通过 click/focus 临时展开。
const isNavSearchExpanded = computed(
  () => isNavSearchClicked.value || isNavSearchFocused.value || navSearchQuery.value.length > 0,
)
const hasNavSearchHistory = computed(() => navSearchHistory.value.length > 0)
const navSearchKeyword = computed(() => navSearchQuery.value.trim())
const hasNavSearchDropdownContent = computed(() => navSearchKeyword.value.length > 0 || hasNavSearchHistory.value)
const isNavSearching = ref(false)
// 空关键词且没有历史记录时只展开输入框，不渲染空的搜索下拉层；有关键词时等 suggest 接口请求完成后再展开。
const showNavSearchDropdown = computed(
  () =>
    isNavSearchExpanded.value &&
    isNavSearchFocused.value &&
    hasNavSearchDropdownContent.value &&
    (navSearchKeyword.value.length === 0 || (!isNavSearching.value && hasNavSearched.value)),
)
const showNavSearchEmpty = computed(
  () =>
    navSearchKeyword.value.length > 0 &&
    !isNavSearching.value &&
    hasNavSearched.value &&
    navPopularRecommendations.value.length === 0,
)

// === 历史标签两行折叠逻辑（基于宽度累加计算） ===
const isNavHistoryExpanded = ref(false)
const hasNavMoreThanTwoLines = ref(false)
const maxNavVisibleIndex = ref(9999)

// tag 样式常量：padding 左右各 12px、gap 12px、logo 宽 20px + gap 4px
const TAG_PADDING = 24
const TAG_GAP = 12
const TAG_LOGO_EXTRA = 24 // logo(20px) + gap(4px)
const TAG_DELETE_EXTRA = 18 // delete(14px) + gap(4px)
const EXPAND_BTN_WIDTH = 30 // 展开按钮宽度
const NAV_DROPDOWN_CONTENT_WIDTH = 416 - 64 // 下拉框宽度 416px - padding 左右各 32px = 352px

let _measureCanvas: HTMLCanvasElement | null = null
const getMeasureCtx = () => {
  if (!_measureCanvas) {
    _measureCanvas = document.createElement('canvas')
  }
  const ctx = _measureCanvas.getContext('2d')
  if (ctx) {
    ctx.font = '400 14px Outfit, sans-serif'
  }
  return ctx
}

const calcNavHistoryLayout = () => {
  const history = navSearchHistory.value
  if (!history || history.length === 0) {
    hasNavMoreThanTwoLines.value = false
    maxNavVisibleIndex.value = 9999
    return
  }

  const ctx = getMeasureCtx()
  if (!ctx) return

  const containerWidth = NAV_DROPDOWN_CONTENT_WIDTH
  let currentLineWidth = 0
  let lineCount = 1
  let thirdLineIndex = -1

  for (let i = 0; i < history.length; i++) {
    const item = history[i]
    const textWidth = ctx.measureText(item.label || '').width
    const logoWidth = item.logo ? TAG_LOGO_EXTRA : 0
    // tag width includes text, padding, logo (if any), and delete button with its flex gap
    const tagWidth = Math.ceil(textWidth + TAG_PADDING + logoWidth + TAG_DELETE_EXTRA + 2) // +2px for safety margin

    // 如果当前行已有元素，需要加上 gap
    const widthNeeded = currentLineWidth > 0 ? TAG_GAP + tagWidth : tagWidth

    let effectiveContainerWidth = containerWidth
    // 为第二行预留展开按钮的宽度以及额外的 20px
    if (lineCount === 2 && i < history.length - 1) {
      effectiveContainerWidth = containerWidth - EXPAND_BTN_WIDTH - 20
    }

    if (currentLineWidth + widthNeeded > effectiveContainerWidth && currentLineWidth > 0) {
      // 换行
      lineCount++
      if (lineCount > 2) {
        thirdLineIndex = i
        break
      }
      currentLineWidth = tagWidth
    } else {
      currentLineWidth += widthNeeded
    }
  }

  if (thirdLineIndex !== -1) {
    hasNavMoreThanTwoLines.value = true
    maxNavVisibleIndex.value = thirdLineIndex
  } else {
    hasNavMoreThanTwoLines.value = false
    maxNavVisibleIndex.value = 9999
  }
}

watch(
  () => navSearchHistory.value,
  () => {
    calcNavHistoryLayout()
  },
  { deep: true, immediate: true },
)

const handleClearNavSearchHistory = () => {
  clearNavSearchHistory()
}

const handleDeleteNavSearchHistory = (index: number) => {
  deleteNavSearchHistory(index)
}

const handleSelectNavSearchHistory = async (history: Readonly<SearchHistoryItem>) => {
  await selectNavSearchHistory(history)
  isNavSearchFocused.value = false
}

const handleSelectNavRecommendation = async (recommendation: Readonly<TraderListItem>) => {
  isNavSearchFocused.value = false
  await selectNavTraderItem(recommendation)
}

// 搜索框展开后等待 DOM 更新完成，再聚焦输入框以显示闪烁光标。
const focusNavSearchInput = async () => {
  await nextTick()

  document.getElementById('navSearchInputRef')?.focus()
}

// 导航搜索聚焦仅负责展开交互，避免空关键词时加载默认推荐接口。
const handleNavSearchInputFocus = () => {
  isNavSearchFocused.value = true
}

// 点击导航搜索壳时，同步展开并进入输入聚焦态。
const handleNavSearchExpand = () => {
  homeTop('Search')
  if (navSearchCollapseTimer) {
    clearTimeout(navSearchCollapseTimer)
    navSearchCollapseTimer = null
  }

  isNavSearchClicked.value = true
  handleNavSearchInputFocus()
  focusNavSearchInput()
}

// 清空搜索框内容并重置搜索状态与建议
const handleClearNavSearchQuery = () => {
  navSearchQuery.value = ''
  if (navSearchDebounceTimer) {
    clearTimeout(navSearchDebounceTimer)
    navSearchDebounceTimer = null
  }
  isNavSearching.value = false
  hasNavSearched.value = false
  void fetchNavSearchSuggest('')
}

// 导航搜索提交时，空关键词只聚焦输入框，避免首页顶部收起态直接跳转搜索页。
const handleNavSearchSubmit = async () => {
  if (navSearchKeyword.value.length === 0) {
    handleNavSearchExpand()
    return
  }

  if (navSearchDebounceTimer) {
    clearTimeout(navSearchDebounceTimer)
    navSearchDebounceTimer = null
  }
  isNavSearching.value = false

  await handleNavSearch()
  // 提交后关闭下拉，避免搜索建议被清空时仍显示空面板。
  isNavSearchFocused.value = false
  document.getElementById('navSearchInputRef')?.blur()
}

let navSearchCollapseTimer: ReturnType<typeof setTimeout> | null = null
let navSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null

// 输入框失焦后，如果没有关键词则回到顶部收起态。
const handleNavSearchBlur = () => {
  handleBaseNavSearchBlur()

  if (navSearchCollapseTimer) {
    clearTimeout(navSearchCollapseTimer)
  }

  navSearchCollapseTimer = setTimeout(() => {
    if (navSearchKeyword.value.length === 0) {
      isNavSearchClicked.value = false
    }
  }, 220)
}

watch(navSearchQuery, (value) => {
  if (navSearchDebounceTimer) {
    clearTimeout(navSearchDebounceTimer)
    navSearchDebounceTimer = null
  }

  const keyword = value.trim()
  if (!keyword) {
    isNavSearching.value = false
    hasNavSearched.value = false
    void fetchNavSearchSuggest('')
    return
  }

  isNavSearching.value = true
  hasNavSearched.value = false
  navSearchDebounceTimer = setTimeout(async () => {
    try {
      await fetchNavSearchSuggest(keyword)
    } finally {
      isNavSearching.value = false
    }
  }, 250)
})

watch(
  () => [isLoggedIn.value, appStore.user?.avatar],
  () => {
    storedUserAvatar.value = readStoredUserAvatar()
  },
  { immediate: true },
)

/** 根据配置的滚动距离同步 Header 背景进度，并处理下载浮层关闭状态。 */
function handleScroll() {
  if (props.scrollFadeDistance > 0) {
    scrollProgress.value = Math.min(Math.max(window.scrollY / props.scrollFadeDistance, 0), 1)
  }

  if (isDownloadAutoVisible.value && window.scrollY > DOWNLOAD_AUTO_POPUP_SCROLL_THRESHOLD) {
    handleCloseDownloadPopover()
  }
}

/** 关闭自动下载弹窗，关闭后恢复原有的 hover 展示行为，并跨页面保存关闭状态。 */
const handleCloseDownloadPopover = () => {
  isDownloadAutoVisible.value = false
  isDownloadPopoverHoverSuppressed.value = true

  try {
    window.localStorage.setItem(DOWNLOAD_AUTO_POPUP_DISMISS_KEY, getNaturalDayKey())
  } catch {
    // localStorage 不可用时，关闭操作仅对当前页面生效。
  }
}

/** 重新悬停下载按钮时，解除关闭操作留下的浮层 hover 抑制。 */
const handleDownloadButtonMouseEnter = () => {
  isDownloadPopoverHoverSuppressed.value = false
  isDownloadAutoVisible.value = true
}

/** 首次进入页面时展示下载弹窗；用户主动关闭后在同源页面中不再自动展示。 */
const initDownloadAutoPopover = () => {
  try {
    if (window.localStorage.getItem(DOWNLOAD_AUTO_POPUP_DISMISS_KEY) === getNaturalDayKey()) {
      isDownloadPopoverHoverSuppressed.value = true
      return
    }

    if (window.scrollY > DOWNLOAD_AUTO_POPUP_SCROLL_THRESHOLD) {
      return
    }

    isDownloadAutoVisible.value = true
  } catch {
    if (window.scrollY > DOWNLOAD_AUTO_POPUP_SCROLL_THRESHOLD) {
      return
    }

    // 浏览器存储不可用时仍允许本次页面展示自动弹窗。
    isDownloadAutoVisible.value = true
  }
}

const homeTop = (item: string) => {
  matomo.trackEvent('search', {
    af_source: '顶部',
  })
  matomo.trackEvent('home_top', {
    af_type: afTypeMap.get(item),
  })
}

onMounted(async () => {
  const serverInjectedAvatar = document.querySelector('[data-auth-avatar]')?.getAttribute('data-auth-avatar-src')
  if (serverInjectedAvatar) {
    storedUserAvatar.value = serverInjectedAvatar
  }

  initSearchHistory()
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll() // 初始化执行一次，以防页面刷新时已经处于滚动状态
  initDownloadAutoPopover()
  document.addEventListener('click', handleDocumentClick, { passive: true })

  const resolvedBrowserLocale = normalizeBrowserLocale(navigator.language)
  browserLocale.value = resolvedBrowserLocale
  await syncBrowserLocalePromptMessages(resolvedBrowserLocale)

  const dismissedDate = window.localStorage.getItem(LANGUAGE_PROMPT_DISMISS_KEY)
  const hasDismissedToday = dismissedDate === getNaturalDayKey()
  showLanguagePrompt.value = Boolean(
    resolvedBrowserLocale && resolvedBrowserLocale !== locale && !hasDismissedToday && !isNewsDetailRoute.value,
  )
})

onUnmounted(() => {
  if (navSearchCollapseTimer) {
    clearTimeout(navSearchCollapseTimer)
  }
  if (navSearchDebounceTimer) {
    clearTimeout(navSearchDebounceTimer)
  }
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleDocumentClick)
})

const getExchangeStatus = (exchange: TraderListItem) => {
  return exchange?.sortedLabel?.find((item) => item?.labelType === 107)
}
const getExchangeYears = (exchange: TraderListItem) => {
  return exchange?.sortedLabel?.find((item) => item?.labelType === 101)
}
</script>

<template>
  <div
    class="pc-top-nav"
    :style="{
      '--scroll-progress': scrollProgress,
    }"
  >
    <div class="pc-top-nav-content w-[clamp(800px,calc(100%-64px),1200px)]">
      <NuxtLink
        :to="localePath('/')"
        target="_blank"
        aria-label="WikiBit Store"
        class="pc-top-nav-content-left cursor-pointer hover:opacity-80"
      >
        <WikiBitStoreLogo
          class="store-logo store-logo--default h-[36px] w-[172px]"
          :class="{ 'store-logo--hidden': shouldShowWhiteLogo }"
          aria-hidden="true"
        />
        <WikiBitStoreWhiteLogo
          class="store-logo store-logo--white h-[36px] w-[172px]"
          :class="{ 'store-logo--visible': shouldShowWhiteLogo }"
          aria-hidden="true"
        />
      </NuxtLink>
      <div class="pc-top-nav-content-right">
        <div class="pc-top-nav-content-action">
          <NuxtLink
            :to="wikiBitHomeUrl"
            external
            target="_blank"
            rel="noopener noreferrer"
            class="wikibit-home-link flex h-[40px] cursor-pointer items-center rounded-[999px] bg-[#f8f8f8] px-[20px] text-[16px] hover:bg-[#f1f1f1]"
            @click="homeTop('wikibitHome')"
          >
            WikiBit Home
          </NuxtLink>
          <div class="auth-entry-slot">
            <div
              v-show="!isLoggedIn"
              data-auth-login
              data-allow-mismatch="style"
              style="display: none"
              class="login-entry-button h-[40px] cursor-pointer hover:bg-[#193c93]"
              role="button"
              tabindex="0"
              @click="openLoginModal"
              @keydown.enter.prevent="openLoginModal"
              @keydown.space.prevent="openLoginModal"
            >
              {{ t('82902') }}
            </div>
            <div
              v-show="isLoggedIn"
              data-auth-user
              data-allow-mismatch="style"
              style="display: none"
              ref="userMenuRef"
              class="user-entry"
              @mouseenter="openUserMenu"
              @mouseleave="closeUserMenu"
              @click="homeTop('usercenter')"
            >
              <button
                class="user-entry-trigger"
                type="button"
                :aria-expanded="isUserMenuOpen"
                aria-haspopup="menu"
                @click.stop="toggleUserMenu"
              >
                <CommonImage
                  v-show="Boolean(resolvedUserAvatar)"
                  data-auth-avatar
                  :src="resolvedUserAvatar"
                  alt=""
                  class="user-entry-avatar"
                  loading="eager"
                  fetch-priority="high"
                />
              </button>
              <div v-if="isUserMenuOpen" class="user-entry-dropdown">
                <template v-for="item in userMenuItems" :key="item.label">
                  <NuxtLink
                    v-if="item.action !== 'logout'"
                    :to="item.href"
                    external
                    target="_blank"
                    rel="noopener noreferrer"
                    class="user-entry-dropdown-item cursor-pointer hover:bg-[#f5f5f5]"
                    :class="{ danger: item.danger }"
                    @click="handleUserMenuItemClick($event, item)"
                  >
                    {{ item.label }}
                  </NuxtLink>
                  <button
                    v-else
                    type="button"
                    class="user-entry-dropdown-item w-full cursor-pointer border-0 bg-transparent text-left hover:bg-[#f5f5f5] rtl:text-right"
                    :class="{ danger: item.danger }"
                    @click="handleUserMenuItemClick($event, item)"
                  >
                    {{ item.label }}
                  </button>
                </template>
              </div>
            </div>
          </div>
          <div class="divider h-[24px] w-[1px]"></div>
          <div
            v-for="(item, index) in actionButtons"
            :key="index"
            class="action-button-wrapper"
            @click="homeTop(item.label)"
            :class="{
              'search-expanded': item.label === 'Search' && isNavSearchExpanded,
            }"
          >
            <!-- Search 按钮：展开态（搜索输入框） -->
            <div
              v-if="item.label === 'Search'"
              class="nav-search-bar size-[38px] cursor-pointer hover:bg-[#f1f1f1]"
              :class="{ 'is-expanded': isNavSearchExpanded }"
              @click="handleNavSearchExpand"
            >
              <SearchIcon
                v-if="isNavSearchExpanded"
                class="nav-search-icon-image ml-[12px] h-[24px] w-[24px] rtl:mr-[12px] rtl:ml-0"
                @click.stop="handleNavSearchSubmit"
              />
              <input
                id="navSearchInputRef"
                v-model="navSearchQuery"
                type="search"
                name="wikibitNavSearch"
                autocomplete="off"
                inputmode="search"
                class="nav-search-input"
                @focus="handleNavSearchInputFocus"
                @blur="handleNavSearchBlur"
                @keydown.enter.prevent="handleNavSearchSubmit"
              />
              <div class="nav-search-icon" @click.stop="handleNavSearchSubmit">
                <SearchIcon
                  v-if="!isNavSearchExpanded"
                  class="nav-search-icon-image"
                  @click.stop="handleNavSearchSubmit"
                />
                <CommonImage
                  v-else-if="navSearchQuery"
                  src="https://resources1.interface003.com/web30/2026/06/26/020c0828-d5bd-4293-a244-718628634c54.png"
                  :alt="item.label"
                  class="nav-search-icon-image cursor-pointer"
                  loading="lazy"
                  object-fit="contain"
                  @click.stop="handleClearNavSearchQuery"
                />
              </div>
            </div>
            <div v-if="item.label === 'Search' && showNavSearchDropdown" class="nav-search-dropdown" @mousedown.prevent>
              <div v-if="!showNavSearchEmpty" class="search-dropdown-content">
                <div v-if="!navSearchKeyword" class="search-dropdown-section search-history-section">
                  <div v-if="hasNavSearchHistory" class="search-dropdown-header">
                    <span class="search-dropdown-title">{{ t('80356') }}</span>
                    <button
                      class="search-history-delete"
                      type="button"
                      aria-label="Clear search history"
                      :disabled="!hasNavSearchHistory"
                      @click.stop="handleClearNavSearchHistory"
                    >
                      <DeleteIcon class="search-history-delete-icon" aria-hidden="true" />
                    </button>
                  </div>
                  <div v-if="hasNavSearchHistory" class="search-history-tags">
                    <div
                      v-for="(history, historyIndex) in navSearchHistory"
                      v-show="isNavHistoryExpanded || historyIndex < maxNavVisibleIndex"
                      :key="`${history.label}-${historyIndex}`"
                      class="search-history-tag"
                      @click="handleSelectNavSearchHistory(history)"
                    >
                      <CommonImage
                        v-if="history.logo"
                        :src="history.logo"
                        :alt="history.label"
                        class="search-history-tag-logo"
                        loading="lazy"
                        object-fit="cover"
                      />
                      <span class="search-history-tag-label">{{ history.label }}</span>
                      <span
                        class="search-history-tag-delete"
                        role="button"
                        style="display: block"
                        :aria-label="`Delete ${history.label} from search history`"
                        @click.stop="handleDeleteNavSearchHistory(historyIndex)"
                      >
                        <CloseIcon class="search-history-tag-delete-icon" aria-hidden="true" />
                      </span>
                    </div>
                    <div
                      v-if="hasNavMoreThanTwoLines"
                      class="search-history-expand"
                      :class="{ 'is-expanded': isNavHistoryExpanded }"
                      @click="isNavHistoryExpanded = !isNavHistoryExpanded"
                    >
                      <ArrowDownIcon class="search-history-expand-icon" />
                    </div>
                  </div>
                </div>
                <div
                  v-if="
                    navSearchHistory.length > 0 &&
                    (navSearchDefaultData?.globalRank?.length ?? 0) > 0 &&
                    !navSearchKeyword
                  "
                  class="search-dropdown-divider"
                ></div>

                <div class="search-dropdown-section popular-recommendation-section">
                  <div
                    v-if="!navSearchKeyword && (navSearchDefaultData?.globalRank?.length ?? 0) > 0"
                    class="search-dropdown-header"
                  >
                    <span class="search-dropdown-title">{{ t('80357') }}</span>
                  </div>
                  <div class="popular-recommendation-list">
                    <div
                      v-for="recommendation in navPopularRecommendations"
                      :key="recommendation.name"
                      class="popular-recommendation-item"
                      @click="handleSelectNavRecommendation(recommendation)"
                    >
                      <div class="popular-recommendation-main">
                        <div
                          class="popular-recommendation-logo"
                          :style="{
                            backgroundColor: recommendation.icon ? 'transparent' : '#25262B',
                          }"
                        >
                          <CommonImage
                            v-if="recommendation.icon"
                            :src="recommendation.icon"
                            :alt="recommendation.name"
                            class="popular-recommendation-logo-image"
                            loading="lazy"
                            object-fit="cover"
                          />
                          <span v-else class="popular-recommendation-logo-fallback">
                            {{ recommendation?.name?.[0]?.toUpperCase() }}
                          </span>
                        </div>
                        <div class="popular-recommendation-meta">
                          <div class="popular-recommendation-name">
                            {{ recommendation.name }}
                          </div>
                          <div v-if="recommendation.status || recommendation.years" class="popular-recommendation-tags">
                            <span
                              v-if="getExchangeStatus(recommendation)"
                              :style="{ color: formatColor(getExchangeStatus(recommendation)?.fontColor) }"
                              class="status-tag"
                            >
                              {{ getExchangeStatus(recommendation)?.content }}
                            </span>
                            <span
                              v-if="recommendation.status && recommendation.years"
                              class="divider-line"
                              aria-hidden="true"
                            ></span>
                            <span
                              v-if="getExchangeYears(recommendation)?.content"
                              :style="{
                                color: formatColor(getExchangeYears(recommendation)?.fontColor),
                              }"
                              class="years-text"
                              >{{ getExchangeYears(recommendation)?.content }}</span
                            >
                          </div>
                        </div>
                      </div>
                      <div v-if="recommendation.score" class="popular-recommendation-score">
                        <StarIcon />
                        <span class="score-text">{{ formatScoreValue(recommendation.score) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="search-dropdown-empty">
                <div>
                  <CommonImage
                    class="mx-auto block h-[130px] w-[230px]"
                    src="https://resources1.interface003.com/web30/2026/05/26/85c4961e-6d37-43cb-af78-7fbdce783fe5.png"
                    alt=""
                    loading="eager"
                    object-fit="contain"
                  />
                  <div class="mt-[16px] text-[16px] text-[#292A2E]">{{ t('80359') }}</div>
                </div>
              </div>
            </div>

            <!-- 非 Search 按钮或 Search 收起态 -->
            <button
              v-if="item.label !== 'Search'"
              type="button"
              class="action-button size-[38px] cursor-pointer hover:bg-[#f1f1f1]"
              :aria-label="item.label"
              @mouseenter="item.label === 'Download' ? handleDownloadButtonMouseEnter() : undefined"
              @click="
                item.label === 'Support'
                  ? handleSupportClick()
                  : item.label === 'Download'
                    ? handleDownloadClick()
                    : undefined
              "
            >
              <GlobalIcon v-if="item.label === 'Language'" class="action-icon" />
              <DownloadIcon v-if="item.label === 'Download'" class="action-icon" />
              <ServiceIcon v-if="item.label === 'Support'" class="action-icon" />
            </button>

            <div
              v-if="item.label === 'Download'"
              class="download-popover"
              :style="{
                paddingLeft: ['ur'].includes(locale) ? '40px' : '20px',
                paddingRight: ['ja', 'hi'].includes(locale) ? '50px' : '20px',
              }"
              :class="{
                'download-popover-auto-visible': isDownloadAutoVisible,
                'download-popover-hover-suppressed': isDownloadPopoverHoverSuppressed,
              }"
            >
              <button
                type="button"
                class="download-popover-close cursor-pointer border-0 bg-transparent p-0 hover:opacity-80"
                aria-label="关闭弹窗"
                @click.stop="handleCloseDownloadPopover"
              >
                <CommonImage
                  src="https://resources1.interface003.com/web30/2026/07/02/e1866379-c778-4f76-9890-f2e3eb5820b8.png"
                  alt="closeIco"
                  class="size-[32px]"
                  width="32"
                  height="32"
                  aria-hidden="true"
                  loading="eager"
                />
              </button>
              <div class="download-popover-qr relative">
                <CommonQrCode v-if="downloadQrUrl" :text="downloadQrUrl" :size="102" class="h-full w-full" />
                <CommonImage
                  v-else
                  src="https://resources1.interface003.com/web30/2026/06/30/fc199559-2afb-4482-b1b6-00454fa931cc.png"
                  alt="QR Code"
                  class="h-full w-full"
                  loading="lazy"
                  object-fit="contain"
                />
                <div v-if="downloadQrUrl" class="absolute inset-0 m-auto size-[35px]">
                  <CommonImage
                    src="https://resources1.interface003.com/web30/2026/08/03/390d0b89-176a-419d-a4c7-e9ecd8b6e47c.png"
                    alt="logo"
                    class="size-full"
                  />
                </div>
              </div>
              <div class="download-popover-info">
                <div class="download-popover-title">{{ t('82711') }}</div>
                <div class="download-popover-desc">{{ t('82528').replace('#', '') }}</div>
                <button type="button" class="download-popover-qr-btn" @click.prevent="handleDownloadClick">
                  {{ t('82490') }}
                </button>
              </div>
            </div>
            <div v-if="item.label === 'Language' && showLanguagePrompt" class="language-switch-tip">
              <div class="language-switch-tip-text">{{ languagePromptText }}</div>
              <div class="language-switch-tip-actions">
                <button type="button" class="language-switch-tip-confirm" @click.prevent="handleSwitchToBrowserLocale">
                  {{ languagePromptConfirmText }}
                </button>
                <button
                  type="button"
                  class="language-switch-tip-close"
                  aria-label="Close language switch prompt"
                  @click.prevent="handleCloseLanguagePrompt"
                >
                  <CommonImage
                    src="https://resources1.interface003.com/web30/2026/06/22/3d446d3d-d161-41af-b055-bbb94f825f75.png"
                    alt=""
                    class="h-[20px] w-[20px]"
                    srcset=""
                  />
                </button>
              </div>
            </div>
            <div v-if="item.label === 'Language'" class="language-popover">
              <div class="language-popover-title">{{ t('82390') }}</div>
              <div class="language-popover-grid">
                <a
                  v-for="(lang, lIndex) in locales"
                  :key="lIndex"
                  :href="switchLocalePath(lang.code)"
                  class="language-item"
                  :class="{ active: lang.code === locale }"
                  @click="handleLanguageItemClick(lang.code)"
                >
                  <span>{{ lang.name }}</span>
                  <svg
                    v-if="lang.code === locale"
                    class="check-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13.3333 4L5.99992 11.3333L2.66658 8"
                      stroke="#3D3D3D"
                      stroke-width="1.33333"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="showLogoutConfirmDialog"
    class="logout-confirm-mask"
    role="presentation"
    @click.self="closeLogoutConfirmDialog"
  >
    <section
      class="logout-confirm-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logoutConfirmTitle"
      aria-describedby="logoutConfirmDescription"
    >
      <header class="logout-confirm-header">
        <h2 id="logoutConfirmTitle" class="logout-confirm-title">{{ t('82396') }}</h2>
        <button
          class="logout-confirm-close"
          type="button"
          aria-label="关闭退出登录确认弹窗"
          @click="closeLogoutConfirmDialog('关闭')"
        >
          <CloseIcon class="logout-confirm-close-icon" aria-hidden="true" />
        </button>
      </header>
      <div class="logout-confirm-content">
        <p id="logoutConfirmDescription" class="logout-confirm-description">
          {{ t('82397') }}
        </p>
      </div>
      <footer class="logout-confirm-footer">
        <button
          class="logout-confirm-button logout-confirm-button-cancel"
          type="button"
          @click="closeLogoutConfirmDialog('取消')"
        >
          {{ t('82398') }}
        </button>
        <button class="logout-confirm-button logout-confirm-button-submit" type="button" @click="confirmLogout">
          {{ t('82399') }}
        </button>
      </footer>
    </section>
  </div>
  <ClientOnly>
    <LazyLogin v-if="appStore.loginShow" />
  </ClientOnly>
</template>
<style scoped lang="less">
@media screen and (max-width: 1200px) {
  .pc-top-nav {
    .pc-top-nav-content-action {
      gap: 16px !important;
    }
  }
}
.pc-top-nav {
  width: calc(100% - var(--wikibit-scrollbar-compensation, 0px));
  min-width: 800px;
  height: 70px;
  position: sticky;
  z-index: 999;
  top: 0;
  inset-inline-start: 0;
  margin-bottom: -70px;
  background-color: rgba(255, 255, 255, var(--scroll-progress, 1));
  transition:
    background-color 0.15s ease-out,
    border-bottom-color 0.15s ease-out;

  .pc-top-nav-content {
    height: 100%;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
  }

  .pc-top-nav-content-left {
    position: relative;
    flex: 0 0 172px;
    display: flex;
    align-items: center;
    width: 172px;
    transition: opacity 0.2s ease;

    .store-logo {
      position: absolute;
      inset-inline-start: 0;
      top: 50%;
      flex-shrink: 0;
      width: 172px;
      height: 36px;
      opacity: 1;
      pointer-events: none;
      transform: translateY(-50%);
      transition: opacity 0.3s ease;
    }

    .store-logo--white,
    .store-logo--hidden {
      opacity: 0;
    }

    .store-logo--visible {
      opacity: 1;
    }
  }

  .pc-top-nav-content-right {
    display: flex;
    align-items: center;
    height: 100%;

    .pc-top-nav-content-action {
      display: flex;
      align-items: center;
      gap: 20px;
      .auth-entry-slot {
        display: flex;
        align-items: center;
        min-height: 40px;
      }

      .login-entry-button {
        min-width: 82px;
        height: 40px;
        padding: 0 20px;
        border-radius: 9999px;
        background: #112d73;
        color: #fff;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        cursor: pointer;
        user-select: none;
        transition:
          background-color 0.2s ease,
          transform 0.2s ease,
          box-shadow 0.2s ease;

        &:hover {
          background: #193c93;
          box-shadow: 0 8px 20px 0 rgba(17, 45, 115, 0.18);
        }

        &:focus-visible {
          outline: 2px solid rgba(17, 45, 115, 0.22);
          outline-offset: 2px;
        }
      }

      .user-entry {
        position: relative;
        display: flex;
        align-items: center;
      }

      .user-entry-trigger {
        width: 38px;
        height: 38px;
        padding: 0;
        border: none;
        border-radius: 50%;
        background: #f8f8f8;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        transition:
          background-color 0.2s ease,
          box-shadow 0.2s ease,
          transform 0.2s ease;

        &:hover {
          background: #f2f4f7;
          transform: translateY(-1px);
        }

        &:focus-visible {
          outline: 2px solid rgba(17, 45, 115, 0.18);
          outline-offset: 2px;
        }
      }

      .user-entry-avatar {
        width: 100%;
        height: 100%;
        flex-shrink: 0;
        border-radius: 50%;
        object-fit: cover;
        background: #d9e2f4;
      }

      .user-entry-avatar-fallback {
        color: #112d73;
        font-size: 16px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .user-entry-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        z-index: 30;
        min-width: 188px;
        padding: 8px 0;
        border-radius: 8px;
        background: #fff;
        box-shadow:
          0 8px 24px 0 rgba(0, 0, 0, 0.1),
          0 16px 48px 0 rgba(0, 0, 0, 0.1);

        &::before {
          content: '';
          position: absolute;
          top: -20px;
          left: 0;
          width: 100%;
          height: 20px;
          background: transparent;
        }
      }

      .user-entry-dropdown-item {
        display: flex;
        align-items: center;
        padding: 12px 24px;
        color: #3d3d3d;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        text-decoration: none;
        transition:
          background-color 0.2s ease,
          color 0.2s ease;

        &:hover {
          background: #f5f5f5;
          color: #112d73;
        }

        &.danger:hover {
          color: #d14343;
        }
      }

      .divider {
        width: 1px;
        height: 24px;
        background-color: rgba(11, 18, 40, 0.14);
      }

      .action-button-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .action-button {
          width: 38px;
          height: 38px;
          padding: 0;
          border: none;
          border-radius: 50%;
          background: #f8f8f8;
          color: inherit;
          font: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition:
            background-color 0.2s ease,
            background 0.2s ease;
          .action-icon {
            width: 24px;
            height: 24px;
            cursor: pointer;

            :deep(path) {
              fill: #292a2e;
              transition: fill 0.2s ease;
            }
          }

          &:hover {
            background:
              linear-gradient(0deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.03) 100%),
              var(--color-background-subtlest, #f8f8f8);
            .action-icon {
              :deep(path) {
                fill: #112d73;
              }
            }
          }

          &:focus-visible {
            outline: 2px solid rgba(17, 45, 115, 0.22);
            outline-offset: 2px;
          }
        }

        /* === 导航搜索栏过渡 === */
        .nav-search-bar {
          display: flex;
          align-items: center;
          width: 38px;
          height: 38px;
          border-radius: 19px;
          background: #f8f8f8;
          overflow: hidden;
          cursor: pointer;
          box-sizing: border-box;
          transition:
            width 0.4s cubic-bezier(0.25, 1, 0.3, 1),
            border-color 0.3s ease,
            background-color 0.3s ease,
            box-shadow 0.3s ease;

          .nav-search-input {
            flex: 1;
            height: 100%;
            border: none;
            outline: none;
            background: transparent;
            font-size: 14px;
            color: #292a2e;
            padding: 0;
            opacity: 0;
            width: 0;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            transition:
              width 0.4s cubic-bezier(0.25, 1, 0.3, 1),
              opacity 0.25s ease 0s,
              padding 0.4s cubic-bezier(0.25, 1, 0.3, 1);

            &::placeholder {
              color: #b7b9be;
              font-size: 14px;
            }
          }

          .nav-search-icon {
            width: 38px;
            height: 38px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition:
              width 0.4s cubic-bezier(0.25, 1, 0.3, 1),
              background-color 0.2s ease,
              background 0.2s ease;

            &:hover {
              background:
                linear-gradient(0deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.03) 100%),
                var(--color-background-subtlest, #f8f8f8);
            }

            .nav-search-icon-image {
              width: 24px;
              height: 24px;
              transition: transform 0.3s ease;
            }
          }

          /* 展开态 */
          &.is-expanded {
            width: 176px;
            border-color: rgba(11, 18, 40, 0.2);
            background: #f8f8f8;
            cursor: text;

            .nav-search-input {
              opacity: 1;
              width: 85px;
              padding: 0 0 0 16px;
              padding-inline-start: 16px;
              padding-inline-end: 0;
              transition:
                width 0.4s cubic-bezier(0.25, 1, 0.3, 1),
                opacity 0.3s ease 0.15s,
                padding 0.4s cubic-bezier(0.25, 1, 0.3, 1);
            }

            .nav-search-icon {
              width: 48px;

              &:hover {
                background: none;
              }

              .nav-search-icon-image {
                width: 24px;
                height: 24px;
              }
            }
          }

          &.is-expanded:focus-within {
            border-color: #112d73;
            box-shadow: 0 0 0 2px rgba(17, 45, 115, 0.12);
          }
        }

        .nav-search-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          z-index: 20;
          width: 416px;
          max-height: 520px;
          padding: 32px 0px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          gap: 20px;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
          box-shadow:
            0 0 1px 0 rgba(30, 31, 33, 0.12),
            0 0 8px 0 rgba(30, 31, 33, 0.16);

          .search-dropdown-content {
            width: 100%;
            min-height: 0;
            display: flex;
            flex: 1 1 auto;
            flex-direction: column;
            // gap: 20px;
          }

          .search-dropdown-empty {
            width: 100%;
            min-height: 0;
            display: flex;
            flex: 1 1 auto;
            align-items: center;
            justify-content: center;
          }

          .search-dropdown-section {
            width: 100%;
            display: flex;
            flex-direction: column;
          }

          .search-history-section {
            gap: 16px;
          }

          .popular-recommendation-section {
            gap: 4px;
            flex: 1 1 auto;
            min-height: 0;
            overflow: hidden;
          }

          .search-dropdown-header {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 32px;
          }

          .search-dropdown-title {
            color: #7d818a;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }

          .search-history-delete {
            width: 14px;
            height: 14px;
            padding: 0;
            border: none;
            background: transparent;
            color: #7d818a;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            .search-history-delete-icon {
              width: 14px;
            }

            &:hover:not(:disabled) {
              color: #ef4444;
            }

            &:disabled {
              opacity: 0.4;
              cursor: default;
            }
          }

          .search-history-tags {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 12px;
            // margin-bottom: 20px;
            padding: 0 32px;
          }

          .search-history-tag,
          .search-history-expand {
            height: 30px;
            padding: 4px 12px;
            border: none;
            border-radius: 1000px;
            background: #f8f8f8;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 4px;
            box-sizing: border-box;
            overflow: visible;
            color: #292a2e;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }

          .search-history-tag {
            max-width: 200px;
            cursor: pointer;
            transition:
              background-color 0.2s ease,
              color 0.2s ease;

            &:hover {
              background: #f1f3f6;
              position: relative;
              z-index: 1;
            }
          }

          .search-history-expand {
            width: 30px;
            padding: 0;
            cursor: pointer;
            transition: background-color 0.2s ease;

            .search-history-expand-icon {
              width: 20px;
              height: 20px;
              transition: transform 0.3s ease;
            }

            &.is-expanded {
              .search-history-expand-icon {
                transform: rotate(180deg);
              }
            }

            &:hover {
              background: #eaecef;
            }
          }

          .search-history-tag-logo {
            width: 20px;
            height: 20px;
            border-radius: 1000px;
            object-fit: cover;
            flex-shrink: 0;
          }

          .search-history-tag-label {
            display: inline-block;
            min-width: 0;
            height: 22px;
            line-height: 22px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .search-history-tag-delete {
            width: 14px;
            opacity: 1;
            padding: 0;
            border: none;
            background: transparent;
            color: #7d818a;
            overflow: visible;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: color 0.2s ease;

            .search-history-tag-delete-icon {
              display: block;
            }
          }

          .search-history-tag-delete:hover {
            color: #292a2e;
          }

          .search-history-empty {
            color: #b7b9be;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }

          .search-dropdown-divider {
            width: calc(100% - 64px);
            margin: 20px 32px;
            height: 1px;
            background: rgba(5, 21, 36, 0.06);
          }

          .popular-recommendation-list {
            width: 100%;
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
              display: none;
            }
          }

          .popular-recommendation-item {
            width: 100%;
            padding: 16px 32px;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            gap: 8px;
            border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
            cursor: pointer;

            &:last-child {
              border-bottom: none;
            }
          }
          .popular-recommendation-item:hover {
            background-color: #f8f8f8;
          }

          .popular-recommendation-main {
            flex: 1 1 0;
            min-width: 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .popular-recommendation-logo {
            width: 32px;
            height: 32px;
            border-radius: 4px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .popular-recommendation-logo-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .popular-recommendation-logo-fallback {
            color: #fff;
            font-size: 16px;
            font-weight: 600;
            line-height: 1;
          }

          .popular-recommendation-meta {
            flex: 1 1 0;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .popular-recommendation-name {
            color: #292a2e;
            font-size: 18px;
            font-weight: 500;
            line-height: 26px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .popular-recommendation-tags {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 400;
            line-height: 18px;
          }

          .status-tag {
          }

          .divider-line {
            width: 1px;
            height: 8px;
            background: rgba(5, 21, 36, 0.12);
            flex-shrink: 0;
          }

          .years-text {
            color: #505258;
          }

          .popular-recommendation-score {
            flex-shrink: 0;
            padding: 4px 12px;
            border-radius: 1000px;
            background: #f8f8f8;
            display: flex;
            align-items: center;
            gap: 4px;

            svg {
              width: 14px;
              height: 14px;
            }

            .score-text {
              color: #292a2e;
              text-align: center;
              font-family: WikiFX;
              font-size: 16px;
              font-weight: 500;
              line-height: 24px;
            }
          }
        }

        .language-popover {
          position: absolute;
          top: calc(100% + 15px);
          inset-inline-end: -20px;
          z-index: 22;
          display: flex;
          width: 801px;
          padding: 40px;
          flex-direction: column;
          align-items: flex-start;
          gap: 20px;
          border-radius: 8px;
          background: #fff;
          box-shadow:
            0 8px 24px 0 rgba(0, 0, 0, 0.1),
            0 16px 48px 0 rgba(0, 0, 0, 0.1);

          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: all 0.2s ease;
          cursor: default;

          &::before {
            content: '';
            position: absolute;
            top: -20px;
            left: 0;
            width: 100%;
            height: 20px;
            background: transparent;
          }

          .language-popover-title {
            color: #292a2e;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 24px;
          }

          .language-popover-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            row-gap: 20px;
            column-gap: 16px;
            width: 100%;
          }

          .language-item {
            text-decoration: none;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 12px;
            border-radius: 4px;
            color: #7d818a;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px;
            cursor: pointer;
            transition:
              background-color 0.2s,
              color 0.2s;

            &:hover {
              background-color: #f5f5f5;
            }

            &.active {
              color: #292a2e;
              background-color: #f5f5f5;
              font-weight: 500;
            }

            .check-icon {
              flex-shrink: 0;
            }
          }
        }

        .download-popover {
          position: absolute;
          top: calc(100% + 15px);
          inset-inline-end: -20px;
          z-index: 23;
          display: flex;
          padding-right: 32px;
          padding: 20px;
          align-items: center;
          border-radius: 8px;
          background: #fff;
          box-shadow:
            0 8px 24px 0 rgba(0, 0, 0, 0.1),
            0 16px 48px 0 rgba(0, 0, 0, 0.1);

          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: all 0.2s ease;
          cursor: default;
          gap: 16px;

          .download-popover-close {
            position: absolute;
            top: 12px;
            inset-inline-end: 12px;
            z-index: 1;
            display: flex;
          }

          &.download-popover-auto-visible {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
          }

          &::before {
            content: '';
            position: absolute;
            top: -20px;
            left: 0;
            width: 100%;
            height: 20px;
            background: transparent;
          }

          .download-popover-qr {
            display: flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            width: 102px;
            height: 102px;
            overflow: hidden;

            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
          }

          .download-popover-info {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .download-popover-title {
            color: #292a2e;
            font-size: 18px;
            font-weight: 600;
            line-height: 26px;
            white-space: nowrap;
          }

          .download-popover-desc {
            color: #505258;
            font-size: 14px;
            font-weight: 500;
            line-height: 22px;
            white-space: nowrap;
          }
          .download-popover-qr-btn {
            background-color: #112d73;
            color: #ffffff;
            border-radius: 1000px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
            padding: 4px 20.5px;
            white-space: nowrap;
            width: fit-content;
          }
        }

        .action-button:hover ~ .language-popover,
        .action-button:focus-visible ~ .language-popover,
        .language-popover:hover,
        .language-popover:focus-within,
        .action-button:hover ~ .download-popover:not(.download-popover-hover-suppressed),
        .action-button:focus-visible ~ .download-popover:not(.download-popover-hover-suppressed),
        .download-popover:not(.download-popover-hover-suppressed):hover,
        .download-popover:not(.download-popover-hover-suppressed):focus-within {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
        }

        .language-switch-tip {
          position: absolute;
          top: calc(100% + 15px);
          left: 50%;
          transform: translateX(-50%);
          z-index: 21;
          width: max-content;
          padding: 12px 20px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          border-radius: 8px;
          background: #fff;
          box-shadow:
            0 0 1px 0 rgba(30, 31, 33, 0.12),
            0 0 8px 0 rgba(30, 31, 33, 0.16);
        }

        .language-switch-tip-text {
          color: #292a2e;
          font-size: 14px;
          font-weight: 400;
          width: 202px;
        }

        .language-switch-tip-actions {
          display: flex;
          align-items: center;

          gap: 16px;
          flex-shrink: 0;
        }

        .language-switch-tip-confirm {
          padding: 4px 16px;
          border: none;
          border-radius: 28px;
          background: #112d73;
          color: #ffffff;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .language-switch-tip-confirm:hover {
          background: linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), #112d73;
        }

        .language-switch-tip-confirm:active {
          background: linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%), #112d73;
        }

        .language-switch-tip-close {
          padding: 0;
          border: none;
          background: transparent;
          color: #292a2e;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          gap: 16px;
        }

        .language-switch-tip-close-icon {
          width: 20px;
          height: 20px;
        }
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .pc-top-nav,
  .pc-top-nav .store-logo {
    transition-duration: 0s;
  }
}

.logout-confirm-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.32);
}

.logout-confirm-dialog {
  width: 440px;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow:
    0 0 1px 0 rgba(0, 0, 0, 0.2),
    0 8px 20px 0 rgba(0, 0, 0, 0.12);
}

.logout-confirm-header {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 24px;
  background: #fff;
}

.logout-confirm-title {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.85);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-confirm-close {
  width: 24px;
  height: 24px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #292a2e;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.logout-confirm-close-icon {
  width: 16px;
  height: 16px;
}

.logout-confirm-content {
  width: 100%;
  box-sizing: border-box;
  padding: 0 24px;
  background: #fff;
}

.logout-confirm-description {
  width: 100%;
  margin: 0;
  color: rgba(0, 0, 0, 0.7);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.logout-confirm-footer {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 24px;
  background: #fff;
}

.logout-confirm-button {
  height: 46px;
  padding: 12px 20px;
  border: none;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;
}

.logout-confirm-button-cancel {
  background: #f8f8f8;
  color: #292a2e;
}

.logout-confirm-button-submit {
  background: #112d73;
  color: #fff;
}

.logout-confirm-button-submit:hover {
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), #112d73;
}

.logout-confirm-button-submit:active {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%), #112d73;
}
html[dir='rtl'] {
  .nav-search-dropdown {
    right: auto;
    left: 0;
    inset-inline-start: auto;
    inset-inline-end: 0;
  }
}
</style>
